import React from 'react';
import PageHeader from '@/components/layout/PageHeader';
import { FORMATIONS } from '@/data/formations';
import Button from '@/components/ui/Button';
import LoadingStatus from '@/components/ui/LoadingStatus';
import Input from '@/components/ui/Input';
import {SavedLineupV2} from './types';

interface SelectionToolbarProps {
  formationName: string;
  onFormationChange: (name: string) => void;
  onClear: () => void;
  saveName: string;
  onSaveNameChange: (value: string) => void;
  matchDate: string;
  onMatchDateChange: (value: string) => void;
  saving: boolean;
  onSave: () => void;
  savedTeams: SavedLineupV2[];
  selectedSavedId: string;
  onLoadSaved: (id: string) => void;
  onDeleteSaved: (id: string) => void;
  playersReady: boolean;
  loadingLineup: boolean;
  lineupError: string;
  savedPage: number;
  savedTotalPages: number;
  onSavedPageChange: (page: number) => void;
  mutationsDisabled: boolean;
}

const SelectionToolbar: React.FC<SelectionToolbarProps> = ({
  formationName,
  onFormationChange,
  onClear,
  saveName,
  onSaveNameChange,
  matchDate,
  onMatchDateChange,
  saving,
  onSave,
  savedTeams,
  selectedSavedId,
  onLoadSaved,
  onDeleteSaved,
  playersReady,
  loadingLineup,
  lineupError,
  savedPage,
  savedTotalPages,
  onSavedPageChange,
  mutationsDisabled,
}) => {
  return (
    <div className="space-y-4">
      <PageHeader title="Team Selection" subtitle="Choose a formation and assign players to positions" />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        {/* Formation and quick actions */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <select
            className="input-field"
            aria-label="Formation"
            value={formationName}
            onChange={(e) => onFormationChange(e.target.value)}
          >
            {FORMATIONS.map((f) => (
              <option key={f.name} value={f.name}>{f.name}</option>
            ))}
          </select>
          <Button variant="outline" onClick={onClear}>Clear</Button>
        </div>

        {/* Save controls */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <Input
            className="w-full sm:w-48"
            placeholder="Lineup name"
            value={saveName}
            onChange={(e) => onSaveNameChange((e.target as HTMLInputElement).value)}
          />
          <Input
            className="w-full sm:w-40"
            type="date"
            aria-label="Match date"
            value={matchDate}
            onChange={(e) => onMatchDateChange((e.target as HTMLInputElement).value)}
          />
          <Button
            onClick={onSave}
            disabled={saving || mutationsDisabled}
            loading={saving}
            loadingLabel="Saving lineup"
            aria-label="Save lineup"
            className="w-full sm:w-auto"
          >
            Save
          </Button>
        </div>

        {/* Load saved lineup */}
        <div className="w-full lg:w-auto">
          <select
            className="input-field w-full lg:w-auto lg:min-w-[200px]"
            aria-label="Load saved lineup"
            value={selectedSavedId}
            disabled={!playersReady || loadingLineup || mutationsDisabled}
            onChange={(e) => onLoadSaved(e.target.value)}
          >
            <option value="">Load saved lineup…</option>
            {savedTeams.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({new Date(t.created_at).toLocaleDateString()})</option>
            ))}
          </select>
          {loadingLineup && <LoadingStatus label="Loading lineup" className="mt-1 text-xs" />}
          {lineupError && <div role="alert" className="mt-1 text-xs text-red-600">{lineupError}</div>}
          {savedTotalPages > 1 && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <Button variant="outline" disabled={savedPage <= 1} onClick={() => onSavedPageChange(savedPage - 1)}>Previous lineups</Button>
              <span>Page {savedPage} of {savedTotalPages}</span>
              <Button variant="outline" disabled={savedPage >= savedTotalPages} onClick={() => onSavedPageChange(savedPage + 1)}>Next lineups</Button>
            </div>
          )}
          {selectedSavedId && (
            <Button
              variant="outline"
              className="mt-2 w-full lg:w-auto"
              aria-label={`Delete ${savedTeams.find(team => team.id === selectedSavedId)?.name || 'lineup'}`}
              onClick={() => onDeleteSaved(selectedSavedId)}
            >
              Delete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectionToolbar;
